// ═══════════════════════════════════════════════════════════════
// WorkChapterModal — Invoque work_chapter depuis l'atelier (S57 C)
// Modal : mode select + instruction textarea + Appliquer
// Crée une nouvelle version. L'user peut restaurer la précédente via drawer.
// ═══════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import { X, Sparkles, Loader } from 'lucide-react';
import { workChapter } from '../../services/bookApi';
import { getTerms } from '../../utils/documentTerminology';

const MODES = [
    { id: 'style',           label: 'Révision stylistique',     hint: 'Ajuster le grain de la prose sans changer l\'argumentaire.' },
    { id: 'density',         label: 'Densité argumentative',    hint: 'Plus dense / plus aéré selon l\'instruction.' },
    { id: 'rewrite_section', label: 'Réécrire une section',     hint: 'Précise la section cible dans l\'instruction.' },
    { id: 'add_section',     label: 'Ajouter une section',      hint: 'Sujet + position dans l\'instruction (par défaut : fin).' },
    { id: 'expand',          label: 'Développer un passage',    hint: 'Passage trop lapidaire à étoffer.' },
    { id: 'trim',            label: 'Élaguer un passage',       hint: 'Passage trop verbeux à resserrer.' },
];

const WorkChapterModal = ({ bookId, book, chapter, onClose, onCompleted }) => {
    const [mode, setMode] = useState('style');
    const [instruction, setInstruction] = useState('');
    const [sectionTitle, setSectionTitle] = useState('');
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const selectedMode = MODES.find(m => m.id === mode);
    const t = getTerms(book?.genre);
    const unitLower = t.unitSingular.toLowerCase();

    const handleApply = async () => {
        if (!instruction.trim() || instruction.length < 3) {
            setError('Instruction trop courte (min 3 caractères).');
            return;
        }
        setError('');
        setRunning(true);
        try {
            const res = await workChapter(bookId, chapter.id, {
                mode,
                instruction: instruction.trim(),
                section_title: sectionTitle.trim() || null,
            });
            setResult(res);
            if (onCompleted) onCompleted(res);
        } catch (err) {
            setError(err.detail?.message || err.message || 'Erreur inconnue');
        } finally {
            setRunning(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} style={{
                width: 560, maxWidth: '92vw', maxHeight: '86vh', overflow: 'auto',
                background: 'var(--card-bg)',
                border: '1px solid var(--card-border)',
                borderRadius: 12, padding: '22px 24px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Sparkles size={18} style={{ color: '#a78bfa' }} />
                        <div>
                            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
                                Réviser {t.docArticle === 'une' ? 'cette' : 'ce'} {unitLower}
                            </h3>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                                {chapter.position}. {chapter.title} · v{chapter.version_number ?? '?'} actuelle · Sonnet 4.6
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                        <X size={18} />
                    </button>
                </div>

                {!result && (
                    <>
                        {/* Mode select */}
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Mode</label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 6 }}>
                                {MODES.map(m => (
                                    <button key={m.id} onClick={() => setMode(m.id)} style={{
                                        padding: '8px 10px', borderRadius: 6, fontSize: 11, textAlign: 'left',
                                        background: mode === m.id ? 'rgba(139,92,246,0.15)' : 'rgba(255,255,255,0.03)',
                                        border: `1px solid ${mode === m.id ? 'rgba(139,92,246,0.4)' : 'rgba(255,255,255,0.06)'}`,
                                        color: mode === m.id ? '#c4b5fd' : 'var(--text-secondary)',
                                        cursor: 'pointer', transition: 'all 0.15s',
                                    }}>
                                        {m.label}
                                    </button>
                                ))}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, fontStyle: 'italic' }}>
                                {selectedMode?.hint}
                            </div>
                        </div>

                        {(mode === 'rewrite_section' || mode === 'add_section') && (
                            <div style={{ marginBottom: 14 }}>
                                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Section cible (optionnel)</label>
                                <input
                                    value={sectionTitle}
                                    onChange={(e) => setSectionTitle(e.target.value)}
                                    placeholder={mode === 'add_section' ? 'Titre à donner à la nouvelle section' : 'Titre exact de la section à réécrire'}
                                    style={{
                                        width: '100%', marginTop: 6, padding: '8px 12px', borderRadius: 6,
                                        background: 'var(--card-bg-secondary)', color: 'var(--text)', fontSize: 12,
                                        border: '1px solid var(--card-border)',
                                    }}
                                />
                            </div>
                        )}

                        {/* Instruction */}
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Instruction</label>
                            <textarea
                                value={instruction}
                                onChange={(e) => setInstruction(e.target.value)}
                                placeholder="Décris ce que tu veux ajuster : style, angle, passages à développer, etc."
                                rows={4}
                                style={{
                                    width: '100%', marginTop: 6, padding: '10px 12px', borderRadius: 6,
                                    background: 'var(--card-bg-secondary)', color: 'var(--text)', fontSize: 12, resize: 'vertical',
                                    border: '1px solid var(--card-border)', lineHeight: 1.5,
                                }}
                            />
                        </div>

                        {error && (
                            <div style={{
                                padding: 10, marginBottom: 12, borderRadius: 6,
                                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                                color: '#ef4444', fontSize: 12,
                            }}>
                                {error}
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
                            <button onClick={onClose} disabled={running} style={{
                                padding: '8px 18px', borderRadius: 6, fontSize: 12,
                                background: 'transparent', color: 'var(--text-secondary)',
                                border: '1px solid var(--card-border)', cursor: 'pointer',
                            }}>Annuler</button>
                            <button onClick={handleApply} disabled={running || !instruction.trim()} style={{
                                padding: '8px 18px', borderRadius: 6, fontSize: 12, fontWeight: 500,
                                background: 'linear-gradient(180deg, rgba(139,92,246,0.25), rgba(139,92,246,0.15))',
                                border: '1px solid rgba(139,92,246,0.4)', color: '#c4b5fd',
                                cursor: running ? 'not-allowed' : 'pointer',
                                display: 'flex', alignItems: 'center', gap: 6,
                                opacity: running ? 0.7 : 1,
                            }}>
                                {running ? <Loader size={12} className="animate-spin" /> : <Sparkles size={12} />}
                                {running ? 'Révision en cours…' : 'Appliquer'}
                            </button>
                        </div>

                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 14, textAlign: 'center' }}>
                            La version précédente sera automatiquement conservée dans l'historique.
                            Tu peux la restaurer à tout moment via le bouton « vN » du header.
                        </div>
                    </>
                )}

                {result && (
                    <div style={{ padding: 16, borderRadius: 8, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#10b981', marginBottom: 10 }}>
                            ✓ Révision appliquée (v{result.version_number})
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                            <div>Mode : <strong>{result.mode}</strong></div>
                            <div>Delta : <strong>{result.delta_chars >= 0 ? '+' : ''}{result.delta_chars} caractères</strong> ({result.previous_char_count} → {result.new_char_count})</div>
                            <div>Durée : <strong>{(result.duration_ms / 1000).toFixed(1)}s</strong></div>
                            <div>Modèle : <strong>{result.model_used}</strong></div>
                            {result.cost_usd != null && <div>Coût : <strong>${Number(result.cost_usd).toFixed(4)}</strong></div>}
                        </div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 14 }}>
                            <button onClick={onClose} style={{
                                padding: '8px 18px', borderRadius: 6, fontSize: 12,
                                background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)',
                                color: '#a78bfa', cursor: 'pointer',
                            }}>Fermer</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WorkChapterModal;
